import { createContext, useContext, useState, type ReactNode } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    type SxProps,
    type Theme
} from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import type { Endpoint, Parameter } from './content';

type DocumentationSearchState = {
    // null while no search is active; otherwise the ids of the sections that match.
    readonly matches: ReadonlySet<string> | null;
};

export const DocumentationSearchContext = createContext<DocumentationSearchState>({ matches: null });

const useIsVisible = (ids: readonly string[]) => {
    const { matches } = useContext(DocumentationSearchContext);

    return !matches || ids.some(id => matches.has(id));
};

const COLLAPSE_AFTER_LINES = 24;

/** Height of the sticky documentation header, measured at runtime (0 when it is not sticky). */
export const DOCUMENTATION_HEADER_HEIGHT_VAR = '--documentation-header-height';
const stickyHeaderOffset = `var(${DOCUMENTATION_HEADER_HEIGHT_VAR}, 0px)`;
// App header (64px on mobile, 60px once scrolled on desktop) + documentation header + breathing room.
export const DOCUMENTATION_SCROLL_OFFSET = `calc(64px + ${stickyHeaderOffset} + 16px)`;
export const DOCUMENTATION_NAV_TOP = `calc(60px + ${stickyHeaderOffset} + 12px)`;

export const CodeBlock = ({ title, children }: { readonly title: string; readonly children: string }) => {
    const { matches } = useContext(DocumentationSearchContext);
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const lineCount = children.split('\n').length;
    const isCollapsible = lineCount > COLLAPSE_AFTER_LINES;
    // Expand while searching so highlighted matches are never hidden behind the fold.
    const isCollapsed = isCollapsible && !expanded && !matches;

    const copy = () => {
        navigator.clipboard.writeText(children)
            .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            });
    };

    return (
        <Paper
            variant='outlined'
            sx={{ overflow: 'hidden', bgcolor: '#101828', color: 'common.white' }}
        >
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ px: 2, py: 0.75, bgcolor: '#1d2939' }}
            >
                <Typography variant='caption' sx={{ color: '#d0d5dd' }}>
                    {title}
                </Typography>
                <Tooltip title={copied ? 'Copied' : 'Copy'}>
                    <IconButton
                        size='small'
                        onClick={copy}
                        aria-label={`Copy ${title}`}
                        className='documentation-print-hidden'
                        sx={{ color: copied ? '#7ee787' : '#d0d5dd' }}
                    >
                        {copied ? <CheckRounded fontSize='small' /> : <ContentCopyRounded fontSize='small' />}
                    </IconButton>
                </Tooltip>
            </Stack>
            <Box sx={{ position: 'relative' }}>
                <Box
                    component='pre'
                    className={isCollapsed ? 'documentation-code-collapsed' : undefined}
                    sx={{
                        m: 0,
                        p: 2.5,
                        overflow: isCollapsed ? 'hidden' : 'auto',
                        maxHeight: isCollapsed ? 320 : 'none',
                        fontSize: 13,
                        lineHeight: 1.7,
                        fontFamily: 'monospace'
                    }}
                >
                    {children}
                </Box>
                {isCollapsed && (
                    <Box
                        className='documentation-print-hidden'
                        sx={{
                            position: 'absolute',
                            insetInline: 0,
                            bottom: 0,
                            height: 72,
                            pointerEvents: 'none',
                            background: 'linear-gradient(rgba(16, 24, 40, 0), #101828)'
                        }}
                    />
                )}
            </Box>
            {isCollapsible && !matches && (
                <Box className='documentation-print-hidden' sx={{ px: 1.5, pb: 1, bgcolor: '#101828' }}>
                    <Button
                        size='small'
                        onClick={() => setExpanded(value => !value)}
                        sx={{ color: '#d0d5dd' }}
                    >
                        {expanded ? 'Show less' : `Show all ${lineCount} lines`}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export const MethodBadge = ({ method }: Pick<Endpoint, 'method'>) => (
    <Chip
        size='small'
        label={method}
        color={method === 'GET' ? 'success' : 'primary'}
        sx={{ fontWeight: 700, minWidth: 58 }}
    />
);

export const ParametersTable = ({ parameters, showRequired = true }: {
    readonly parameters: readonly Parameter[];
    readonly showRequired?: boolean;
}) => (
    <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
            <TableHead>
                <TableRow>
                    <TableCell>{showRequired ? 'Parameter' : 'Field'}</TableCell>
                    <TableCell>Type</TableCell>
                    {showRequired && <TableCell>Required</TableCell>}
                    <TableCell>Description</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {parameters.map(parameter => (
                    <TableRow key={parameter.name}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box component='code'>{parameter.name}</Box>
                        </TableCell>
                        <TableCell>{parameter.type}</TableCell>
                        {showRequired && <TableCell>{parameter.required ? 'Yes' : 'No'}</TableCell>}
                        <TableCell>{parameter.description}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

export const SimpleTable = ({ headers, rows, sx }: {
    readonly headers: readonly string[];
    readonly rows: readonly (readonly ReactNode[])[];
    readonly sx?: SxProps<Theme>;
}) => (
    <TableContainer component={Paper} variant='outlined' sx={sx}>
        <Table size='small'>
            <TableHead>
                <TableRow>
                    {headers.map(header => <TableCell key={header}>{header}</TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                        {row.map((cell, cellIndex) => <TableCell key={cellIndex}>{cell}</TableCell>)}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

export const SubHeading = ({ children }: { readonly children: ReactNode }) => (
    <Typography variant='h6' sx={{ mt: 4, mb: 1.5 }}>{children}</Typography>
);

/**
 * A searchable block of documentation. Search matches against the rendered text of the
 * whole section, so everything inside it (tables, notes, code samples) is findable.
 */
export const DocSection = ({ id, divider = true, sx, children }: {
    readonly id: string;
    readonly divider?: boolean;
    readonly sx?: SxProps<Theme>;
    readonly children: ReactNode;
}) => {
    const isVisible = useIsVisible([id]);

    return (
        <Box
            component='section'
            id={id}
            data-doc-section=''
            sx={{ scrollMarginTop: DOCUMENTATION_SCROLL_OFFSET, display: isVisible ? 'block' : 'none' }}
        >
            {divider && <Divider />}
            <Box sx={[{ py: 4 }, ...(Array.isArray(sx) ? sx : [sx])]}>{children}</Box>
        </Box>
    );
};

/** Group heading that disappears when a search leaves none of its sections visible. */
export const DocGroup = ({ title, description, sectionIds, children }: {
    readonly title: string;
    readonly description: string;
    readonly sectionIds: readonly string[];
    readonly children: ReactNode;
}) => {
    const isVisible = useIsVisible(sectionIds);

    return (
        <Box sx={{ display: isVisible ? 'block' : 'none' }}>
            <Divider />
            <Typography variant='h3' sx={{ pt: 4 }}>{title}</Typography>
            <Typography color='text.secondary' sx={{ mt: 1 }}>{description}</Typography>
            {children}
        </Box>
    );
};

export const EndpointSection = ({ endpoint, compact = false }: {
    readonly endpoint: Endpoint;
    readonly compact?: boolean;
}) => (
    <DocSection id={endpoint.id} sx={{ py: compact ? 3 : 4 }}>
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1.5 }}>
            <MethodBadge method={endpoint.method} />
            <Typography variant={compact ? 'h5' : 'h4'}>{endpoint.title}</Typography>
        </Stack>
        <Box
            component='code'
            sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.75,
                mb: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'text.primary',
                wordBreak: 'break-all'
            }}
        >
            {endpoint.path}
        </Box>
        <Typography color='text.secondary'>{endpoint.description}</Typography>
        {endpoint.note && <Alert severity='info' sx={{ mt: 2 }}>{endpoint.note}</Alert>}
        {endpoint.parameters && (
            <Box sx={{ mt: 3 }}>
                <ParametersTable parameters={endpoint.parameters} />
            </Box>
        )}
        {endpoint.request && (
            <Box sx={{ mt: 3 }}>
                <CodeBlock title='Request'>{endpoint.request}</CodeBlock>
            </Box>
        )}
        {endpoint.response && (
            <Box sx={{ mt: 2 }}>
                <CodeBlock title='Response'>{endpoint.response}</CodeBlock>
            </Box>
        )}
        {endpoint.responseParameters && (
            <>
                <SubHeading>Response fields</SubHeading>
                <ParametersTable parameters={endpoint.responseParameters} showRequired={false} />
            </>
        )}
        {!!endpoint.responseExamples?.length && (
            <Stack spacing={2} sx={{ mt: 2 }}>
                {endpoint.responseExamples.map(example => (
                    <CodeBlock key={example.title} title={example.title}>{example.content}</CodeBlock>
                ))}
            </Stack>
        )}
        {endpoint.statusCodes && (
            <>
                <SubHeading>Response codes</SubHeading>
                <SimpleTable
                    headers={['HTTP status', 'Meaning']}
                    rows={endpoint.statusCodes.map(([code, description]) => [<Box component='code'>{code}</Box>, description])}
                />
            </>
        )}
    </DocSection>
);
